import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import constructionBackgroundImg from '../../assets/images/ConstructionEducationBackground.webp';
import { educationalPageStyles, educationalPageStructure } from '../../context/EducationalPageRules';
import BackButton from '../../components/common/BackButton';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: url(${constructionBackgroundImg}) no-repeat center center;
  background-size: cover;
  position: relative;
  padding: 2rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${educationalPageStructure.layout.backgroundProperties.overlay};
    z-index: 1;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 2;
  max-width: ${educationalPageStyles.spacing.containerMaxWidth};
  margin: 0 auto;
  padding: ${educationalPageStyles.spacing.contentPadding};
  background: ${educationalPageStyles.colors.background};
  border-radius: 8px;
  margin-top: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #9575cd;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #9575cd;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const SubTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #9575cd;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #ffffff;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 2rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.6;
  color: #ffffff;
`;

const EarthworkPage: React.FC = () => {
  const navigate = useNavigate();

  const handleBackToConstruction = () => {
    navigate('/education/construction', {
      state: { 
        selectedCategory: 'Site Development',
        autoExpand: true
      }
    });
  };

  return (
    <PageContainer>
      <BackButton onClick={handleBackToConstruction} />
      <ContentContainer>
        <Title>Understanding Earthwork: A Foundational Overview</Title>
        
        <Section>
          <SectionTitle>Introduction: What is Earthwork?</SectionTitle>
          <Paragraph>
            At its core, Earthwork is the engineering process of moving or processing quantities of soil, rock, or other earth materials. Think of it as reshaping the land. Why do we do this? Primarily, earthwork prepares a site for a specific purpose – whether it's building a foundation, creating a road, shaping a landscape, managing water flow, or rehabilitating land. It's one of the very first and most fundamental stages in many construction and land development projects.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Scope & Applications: Where is Earthwork Used?</SectionTitle>
          <Paragraph>Earthwork isn't just about digging holes for buildings. Its principles and practices are applied across a wide range of fields:</Paragraph>
          <List>
            <ListItem>Construction: Preparing building sites, creating foundations, grading for roads and infrastructure.</ListItem>
            <ListItem>Civil Engineering: Building dams, levees, canals, embankments, and large-scale infrastructure.</ListItem>
            <ListItem>Landscaping & Land Management: Shaping terrain for aesthetic or functional purposes (e.g., creating berms, ponds, level areas).</ListItem>
            <ListItem>Agriculture: Grading fields for irrigation and drainage.</ListItem>
            <ListItem>Environmental Rehabilitation: Capping landfills, restoring mining sites, controlling erosion.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>The Earthwork Process: A Step-by-Step Look</SectionTitle>
          <Paragraph>While every project is unique, the earthwork process generally follows a logical sequence:</Paragraph>
          
          <SubTitle>Planning & Assessment:</SubTitle>
          <List>
            <ListItem>Site Investigation: Understanding the existing ground conditions, including soil types, rock formations, and the water table level through methods like soil testing and surveying.</ListItem>
            <ListItem>Design: Planning the desired final shape of the land (grades, slopes, elevations).</ListItem>
            <ListItem>Surveying & Layout: Marking out the areas for excavation (cutting) and filling on the site.</ListItem>
          </List>

          <SubTitle>Site Preparation:</SubTitle>
          <List>
            <ListItem>Clearing & Grubbing: Removing vegetation (trees, shrubs, roots) and any existing obstacles from the work area.</ListItem>
            <ListItem>Topsoil Management: Stripping and stockpiling valuable topsoil for later reuse.</ListItem>
          </List>

          <SubTitle>Execution - Moving the Earth:</SubTitle>
          <List>
            <ListItem>Excavation ("Cutting"): Digging out earth material from areas that are too high.</ListItem>
            <ListItem>Loading & Hauling: Using heavy equipment (like excavators, loaders, scrapers, trucks) to move the excavated material.</ListItem>
            <ListItem>Placement & Filling: Depositing earth material in areas that are too low to bring them up to the desired grade.</ListItem>
            <ListItem>Grading & Shaping: Spreading and leveling the placed fill material to achieve the planned slopes and elevations.</ListItem>
          </List>

          <SubTitle>Stabilization & Finishing:</SubTitle>
          <List>
            <ListItem>Compaction: Using rollers or other equipment to increase the density of the placed fill material.</ListItem>
            <ListItem>Drainage: Installing systems to manage surface water and groundwater.</ListItem>
            <ListItem>Final Grading & Topsoil Reinstatement: Fine-tuning the surface grade and replacing stockpiled topsoil.</ListItem>
            <ListItem>Erosion Control: Implementing measures to protect the disturbed soil.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Key Considerations & Principles</SectionTitle>
          <List>
            <ListItem>Soil Mechanics: Different soil types behave differently when excavated, loaded, compacted, and exposed to water.</ListItem>
            <ListItem>Equipment: Selecting and efficiently using the right heavy machinery is crucial for productivity.</ListItem>
            <ListItem>Water Control: Managing groundwater and surface water runoff is essential for stability.</ListItem>
            <ListItem>Calculations (Cut & Fill): Accurately calculating material volumes is fundamental for planning.</ListItem>
            <ListItem>Compaction: Achieving specified density is critical for long-term stability.</ListItem>
            <ListItem>Safety: Protocols are paramount when working with heavy machinery and unstable excavations.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Quantification & Cost Estimating</SectionTitle>
          <Paragraph>A major component, especially in construction, is accurately estimating the cost of earthwork. This involves:</Paragraph>
          <List>
            <ListItem>Quantity Take-off: Measuring volumes of cut, fill, topsoil stripping, clearing, etc.</ListItem>
            <ListItem>Costing: Assigning costs based on labor, equipment rates, materials, and project overheads.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Importance & Connections: The Bigger Picture</SectionTitle>
          <Paragraph>
            Earthwork is more than just moving dirt; it's the foundation upon which projects are built. Proper earthwork ensures the stability and longevity of structures, impacts project costs and schedules significantly, and requires adherence to engineering principles and best practices for safety and quality. It connects directly to fields like Geotechnical Engineering, Construction Management, Civil Engineering, and Environmental Science.
          </Paragraph>
          <Paragraph>
            Mastering the principles and practices of earthwork is essential for anyone involved in shaping the land for human use.
          </Paragraph>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default EarthworkPage; 